import { useEffect, useMemo, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { LogOut, Trash2, RefreshCw, Mail, Phone, List, BarChart3 } from "lucide-react";
import { toast, Toaster } from "sonner";

const STATUSES = ["new", "contacted", "quoted", "won", "lost"];
const MONTH_LABELS = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function monthKey(iso) {
  return typeof iso === "string" ? iso.slice(0, 7) : "unknown";
}
function monthLabel(key) {
  const [y, m] = key.split("-");
  return `${MONTH_LABELS[Number(m) - 1] || key} ${y}`;
}
function tally(list, keyFn) {
  const map = new Map();
  list.forEach((x) => {
    const k = keyFn(x) || "Unspecified";
    map.set(k, (map.get(k) || 0) + 1);
  });
  return Array.from(map.entries()).sort((a, b) => b[1] - a[1]);
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("list"); // "list" | "report"

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch (e) { toast.error("Could not load leads"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  if (user === null) return <div className="pt-40 wrap">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin-login" replace />;

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}/status?status=${status}`);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch { toast.error("Could not update"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
    } catch { toast.error("Could not delete"); }
  };

  return (
    <div data-testid="page-admin" className="pt-24 min-h-screen bg-[var(--bg-2)]">
      <Toaster richColors position="top-right" />
      <div className="wrap py-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="eyebrow eyebrow-red">Admin · Leads</div>
            <h1 className="h-display text-4xl md:text-5xl uppercase mt-2">Mockup requests</h1>
            <p className="text-sm text-neutral-500 mt-1">Signed in as {user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:inline-flex rounded-full bg-neutral-200/60 p-1">
              <button data-testid="admin-view-list" onClick={() => setView("list")} className={`px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${view === "list" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-600"}`}><List size={13}/> Leads</button>
              <button data-testid="admin-view-report" onClick={() => setView("report")} className={`px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${view === "report" ? "bg-white text-neutral-900 shadow-sm" : "text-neutral-600"}`}><BarChart3 size={13}/> Monthly report</button>
            </div>
            <button onClick={load} data-testid="admin-refresh" className="btn-ghost"><RefreshCw size={16}/> Refresh</button>
            <button onClick={logout} data-testid="admin-logout" className="btn-dark"><LogOut size={16}/> Sign out</button>
          </div>
        </div>

        {view === "report" ? (
          <ReportView leads={leads} loading={loading} />
        ) : (
          <ListView
            leads={leads}
            loading={loading}
            filter={filter}
            setFilter={setFilter}
            updateStatus={updateStatus}
            remove={remove}
          />
        )}

        <p className="mt-6 text-xs text-neutral-500">Emails are sent to {process.env.REACT_APP_NOTIFY_EMAIL || "Quotes@928wraps.com"} on every new submission. <Link to="/" className="underline">Back to site</Link></p>
      </div>
    </div>
  );
}

function ListView({ leads, loading, filter, setFilter, updateStatus, remove }) {
  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: leads.filter((l) => l.status === s).length }), {});

  return (
    <>
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-6 gap-3">
          <button onClick={() => setFilter("all")} className={`flat-card rounded-xl p-4 text-left ${filter === "all" ? "!border-[var(--red)]" : ""}`}>
            <div className="eyebrow">Total</div>
            <div className="font-display text-3xl mt-1">{leads.length}</div>
          </button>
          {STATUSES.map((s) => (
            <button key={s} onClick={() => setFilter(s)} className={`flat-card rounded-xl p-4 text-left ${filter === s ? "!border-[var(--red)]" : ""}`}>
              <div className="eyebrow capitalize">{s}</div>
              <div className="font-display text-3xl mt-1">{counts[s]}</div>
            </button>
          ))}
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-neutral-200 overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-neutral-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center text-neutral-500">No leads yet. When someone submits the mockup form, they&apos;ll appear here.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[var(--bg-2)] text-neutral-500">
                  <tr className="text-left">
                    <th className="p-4 eyebrow">When</th>
                    <th className="p-4 eyebrow">Contact</th>
                    <th className="p-4 eyebrow">Service</th>
                    <th className="p-4 eyebrow">Vehicle</th>
                    <th className="p-4 eyebrow">Details</th>
                    <th className="p-4 eyebrow">Status</th>
                    <th className="p-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l) => (
                    <tr key={l.id} data-testid={`lead-row-${l.id}`} className="border-t border-neutral-100 align-top">
                      <td className="p-4 text-neutral-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}<br/><span className="text-xs">{new Date(l.created_at).toLocaleTimeString()}</span></td>
                      <td className="p-4">
                        <div className="font-semibold">{l.full_name}</div>
                        <div className="text-neutral-500">{l.business_name || "—"}</div>
                        <div className="mt-2 flex flex-col gap-1 text-xs">
                          <a href={`mailto:${l.email}`} className="flex items-center gap-1 text-neutral-700 hover:text-[var(--red)]"><Mail size={12}/>{l.email}</a>
                          <a href={`tel:${l.phone}`} className="flex items-center gap-1 text-neutral-700 hover:text-[var(--red)]"><Phone size={12}/>{l.phone}</a>
                        </div>
                      </td>
                      <td className="p-4">{l.project_type}<br/><span className="text-xs text-neutral-500">{l.budget_range || "—"} · {l.timeline || "—"}</span></td>
                      <td className="p-4">{l.vehicle_type}</td>
                      <td className="p-4 max-w-[280px]"><div className="line-clamp-3 text-neutral-700">{l.project_details}</div></td>
                      <td className="p-4">
                        <select data-testid={`status-${l.id}`} value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} className="input-928 !py-2 !px-3 !text-xs capitalize w-32">
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="p-4">
                        <button onClick={() => remove(l.id)} className="w-9 h-9 rounded-full border border-neutral-200 grid place-items-center text-neutral-500 hover:text-[var(--red)] hover:border-[var(--red)]" aria-label="Delete"><Trash2 size={14}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
    </>
  );
}

function ReportView({ leads, loading }) {
  const byMonth = useMemo(() => {
    const map = new Map();
    leads.forEach((l) => {
      const k = monthKey(l.created_at);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(l);
    });
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [leads]);

  const exportCsv = () => {
    const rows = [["Month","Total","Won","Contacted","Service","Service count","Source","Source count"]];
    byMonth.forEach(([month, list]) => {
      const services = tally(list, (l) => l.project_type);
      const sources = tally(list, (l) => l.referral_source);
      const won = list.filter((l) => l.status === "won").length;
      const contacted = list.filter((l) => l.status === "contacted").length;
      const rowCount = Math.max(services.length, sources.length, 1);
      for (let i = 0; i < rowCount; i++) {
        rows.push([
          i === 0 ? monthLabel(month) : "",
          i === 0 ? String(list.length) : "",
          i === 0 ? String(won) : "",
          i === 0 ? String(contacted) : "",
          services[i]?.[0] || "",
          services[i] ? String(services[i][1]) : "",
          sources[i]?.[0] || "",
          sources[i] ? String(sources[i][1]) : "",
        ]);
      }
    });
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `928wraps-leads-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="mt-8 p-10 text-center text-neutral-500 bg-white rounded-2xl border border-neutral-200">Loading…</div>;
  if (byMonth.length === 0) return <div className="mt-8 p-10 text-center text-neutral-500 bg-white rounded-2xl border border-neutral-200">No leads yet.</div>;

  return (
    <div className="mt-8 space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-sm text-neutral-500">{leads.length} lead{leads.length !== 1 ? "s" : ""} across {byMonth.length} month{byMonth.length !== 1 ? "s" : ""}</div>
        <button onClick={exportCsv} data-testid="report-export-csv" className="btn-ghost text-xs">Export CSV</button>
      </div>

      {byMonth.map(([month, list]) => {
        const services = tally(list, (l) => l.project_type);
        const sources = tally(list, (l) => l.referral_source);
        const statusCounts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: list.filter((l) => l.status === s).length }), {});
        return (
          <div key={month} data-testid={`report-month-${month}`} className="bg-white rounded-2xl border border-neutral-200 overflow-hidden">
            <div className="p-6 md:p-8 border-b border-neutral-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <div className="eyebrow eyebrow-red">{monthLabel(month)}</div>
                <div className="font-display text-3xl md:text-4xl mt-1">{list.length} lead{list.length !== 1 ? "s" : ""}</div>
              </div>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <div key={s} className="px-3 py-2 rounded-xl bg-[var(--bg-2)] border border-neutral-200 text-xs">
                    <span className="eyebrow capitalize">{s}</span>
                    <span className="ml-2 font-display text-base">{statusCounts[s]}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-neutral-100">
              <div className="p-6 md:p-8">
                <div className="eyebrow">By service</div>
                <ul className="mt-4 space-y-2.5">
                  {services.map(([svc, n]) => {
                    const pct = Math.round((n / list.length) * 100);
                    return (
                      <li key={svc}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-neutral-800">{svc}</span>
                          <span className="text-neutral-500"><span className="font-display text-base text-neutral-900">{n}</span> · {pct}%</span>
                        </div>
                        <div className="mt-1.5 h-1.5 bg-neutral-100 rounded-full overflow-hidden"><div className="h-full bg-[var(--red)]" style={{ width: `${pct}%` }}/></div>
                      </li>
                    );
                  })}
                </ul>
              </div>

              <div className="p-6 md:p-8">
                <div className="eyebrow">By source</div>
                <ul className="mt-4 space-y-2.5">
                  {sources.map(([src, n]) => {
                    const pct = Math.round((n / list.length) * 100);
                    return (
                      <li key={src}>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-neutral-800">{src}</span>
                          <span className="text-neutral-500"><span className="font-display text-base text-neutral-900">{n}</span> · {pct}%</span>
                        </div>
                        <div className="mt-1.5 h-1.5 bg-neutral-100 rounded-full overflow-hidden"><div className="h-full bg-neutral-900" style={{ width: `${pct}%` }}/></div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
